import sanitizeHtml from 'sanitize-html';

// Delimiters only convert at word boundaries, so mid-word "*"/"_" stay
// literal ("5*8 machines", "SAP_HANA"). The `(?!\2)` tempering makes the
// nearest valid closer win ("**9** and **8**" must not merge into one span)
// and bounds backtracking. Bold must run before italic so "**" is never
// consumed as two "*" delimiters.
const BOLD_PATTERN = /(^|[^\w*_])(\*\*|__)(?!\s)((?:(?!\2)[\s\S])*?(?:(?!\2)\S))\2(?=$|[^\w*_])/g;
const ITALIC_PATTERN = /(^|[^\w*_])([*_])(?!\s)((?:(?!\2)[\s\S])*?(?:(?!\2)\S))\2(?=$|[^\w*_])/g;

// Above this length emphasis conversion is skipped (input is still sanitized)
// to bound worst-case regex time on adversarial delimiter-heavy input.
const EMPHASIS_LENGTH_CAP = 10_000;

// Must mirror `allowedTags` in renderScoreMarkdown below. Any other "<" is
// escaped before sanitizing: sanitize-html parses an unclosed "<" + letter as
// a tag opening and silently eats the rest of the string ("CAD<CAM" -> "CAD").
const UNKNOWN_TAG_OPEN_PATTERN = /<(?!\/?(?:p|br|b|strong|em|i|ul|ol|li)\b)/gi;

function convertInlineEmphasis(value: string): string {
  return value
    .replace(BOLD_PATTERN, '$1<strong>$3</strong>')
    .replace(ITALIC_PATTERN, '$1<em>$3</em>');
}

/**
 * Renders score text (inline Markdown emphasis and/or legacy HTML) into HTML
 * safe for `v-html` (ONE-6616).
 *
 * The text is candidate-influenced model output, so it must never produce
 * interactive content: the allowlist is deliberately narrow (no `a[href]`,
 * which sanitize-html allows by default), and any `<` that does not open an
 * allowlisted tag is pre-escaped so unknown tags render as inert visible text.
 *
 * Only bold/italic convert — fuller Markdown would mangle literal "*", "_",
 * "#", and "N." in this prose, so everything else stays literal.
 *
 * Output may contain block elements (`<p>`, `<ul>`): bind it into a `<div>`,
 * not a `<p>`.
 */
export function renderScoreMarkdown(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const escaped = value.replace(UNKNOWN_TAG_OPEN_PATTERN, '&lt;');
  const converted = value.length > EMPHASIS_LENGTH_CAP ? escaped : convertInlineEmphasis(escaped);

  return sanitizeHtml(converted, {
    allowedTags: ['p', 'br', 'b', 'strong', 'em', 'i', 'ul', 'ol', 'li'],
    allowedAttributes: {},
  });
}
